import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Eye, Pencil, ArrowLeft, Save, Send, Sparkles, BookOpenCheck } from 'lucide-react'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { lessonApi, wordToLessonItems } from '../api/lessonBuilderApi'
import DraggablePalette from '../components/DraggablePalette'
import SectionCanvas from '../components/SectionCanvas'
import LessonMetaForm from '../components/LessonMetaForm'
import BuilderPreview from '../components/BuilderPreview'
import LessonCardRenderer from '../components/LessonCardRenderer'
import FileDropzone from '../components/FileDropzone'
import { buildSampleBai1 } from '../lib/sampleLesson'
import { genId } from '../lib/id'
import { GeoGebraService } from '../../../services/video/GeoGebraService'
import type { CardType, SimulationCard } from '../types/LessonDoc'

const DOCX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

const LessonBuilderPage: React.FC = () => {
  const { lesson, selectedSectionId, addCard, addRichCard, addPreparedCard, setMeta, newLesson, loadLesson } = useLessonBuilder()
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [previewSec, setPreviewSec] = useState<string>('all')
  const { id } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    if (id) {
      lessonApi.get(id).then((doc) => {
        if (doc) loadLesson(doc)
      })
    } else {
      newLesson()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSave = async (publish: boolean) => {
    if (!lesson.meta.tieuDe.trim()) {
      alert('Vui lòng nhập tiêu đề bài học trước khi lưu.')
      return
    }
    const saved = await lessonApi.save(lesson)
    loadLesson(saved)
    setSavedAt(new Date().toLocaleTimeString('vi-VN'))
    if (publish) {
      const pub = await lessonApi.publish(saved.id)
      navigate(`/bai-giang/${(pub || saved).slug}`)
    } else if (!id) {
      navigate(`/soan-bai/${saved.id}`, { replace: true })
    }
  }

  const handleWordImport = async (file: File) => {
    setAiBusy(true)
    setAiError(null)
    try {
      const res: any = await wordToLessonItems(file)
      if (Array.isArray(res?.items) && res.items.length > 0) {
        addRichCard(res.items, res.title)
        // Tự động gắn mô phỏng GeoGebra đã có của bài (theo Lớp/Chương/Bài)
        const lop = Number(lesson.meta.lop), chuong = Number(lesson.meta.chuong), bai = Number(lesson.meta.bai)
        if (lop && chuong && bai) {
          GeoGebraService.getByLesson(lop, chuong, bai).forEach((g) => {
            const sim: SimulationCard = { id: genId(), type: 'simulation', kind: 'geogebra', ggbFile: g.filename, title: g.title, height: 520 }
            addPreparedCard(sim, /chính/i)
          })
        }
      } else if (res?.html) {
        // Backend đang ở BẢN CŨ (trả HTML). Tạm bọc vào 1 ô text để không mất dữ liệu.
        addRichCard([{ type: 'text', content: 'Backend đang ở bản cũ (trả HTML). Hãy deploy lại backend mới để có nội dung tương tác.' } as any], res.title)
        setAiError('Backend chưa cập nhật: đang trả HTML thay vì nội dung tương tác. Cần deploy lại backend mới.')
      } else {
        setAiError('AI không trả về nội dung. Kiểm tra log backend (pm2 logs python-backend) và API key.')
      }
    } catch (e) {
      setAiError((e as Error)?.message || 'Không tạo được bài học từ Word')
    } finally {
      setAiBusy(false)
    }
  }

  // Bấm chip ở kho thẻ → thêm vào phần đang chọn (mặc định phần đầu)
  const handlePaletteAdd = (type: CardType) => {
    const target = lesson.sections.find((s) => s.id === selectedSectionId) || lesson.sections[0]
    if (target) addCard(target.id, type)
  }

  const previewSection = lesson.sections.find((s) => s.id === previewSec)

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Thanh tiêu đề */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/elearning')} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-black text-gray-900">Soạn bài giảng</h1>
          <button
            onClick={() => {
              if (lesson.sections.some((s) => s.cards.length) && !confirm('Tải bài mẫu sẽ thay nội dung hiện tại?')) return
              loadLesson(buildSampleBai1())
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100"
          >
            <BookOpenCheck size={14} /> Tạo bài mẫu
          </button>
          {savedAt && <span className="text-xs text-gray-400">Đã lưu lúc {savedAt}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleSave(false)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
            <Save size={16} /> Lưu nháp
          </button>
          <button onClick={() => handleSave(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700">
            <Send size={16} /> Xuất bản
          </button>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button onClick={() => setMode('edit')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === 'edit' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>
              <Pencil size={15} /> Soạn
            </button>
            <button onClick={() => setMode('preview')} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === 'preview' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>
              <Eye size={15} /> Toàn màn hình
            </button>
          </div>
        </div>
      </div>

      {mode === 'preview' ? (
        <div className="max-w-3xl mx-auto p-6">
          <BuilderPreview lesson={lesson} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4">
          {/* Trái: thông tin + Word-AI + kho thẻ kéo được */}
          <aside className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Thông tin bài</h3>
              <LessonMetaForm meta={lesson.meta} onChange={setMeta} />
            </div>
            <div className="bg-white rounded-xl border-2 border-violet-200 p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-black text-violet-700 uppercase tracking-wider mb-2">
                <Sparkles size={14} /> Tạo bài học từ Word (AI)
              </h3>
              <FileDropzone
                accept={DOCX_ACCEPT}
                onFile={handleWordImport}
                busy={aiBusy}
                label="Kéo-thả .docx → AI tạo bài tương tác"
                hint="pmaker chuyển MathType→công thức, tách ảnh (~10-20s)"
              />
              {aiError && <p className="text-xs text-red-600 mt-2">{aiError}</p>}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 sticky top-20">
              <DraggablePalette onAdd={handlePaletteAdd} />
              <p className="text-[11px] text-gray-400 mt-3">💡 Kéo thẻ thả vào một phần ở giữa, hoặc bấm để thêm vào phần đầu.</p>
            </div>
          </aside>

          {/* Giữa: canvas các phần (vùng thả) */}
          <section className="lg:col-span-5">
            <SectionCanvas />
          </section>

          {/* Phải: xem trước (từng phần / toàn bài) */}
          <section className="lg:col-span-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider">Xem trước</h3>
                <select
                  value={previewSec}
                  onChange={(e) => setPreviewSec(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none bg-white"
                >
                  <option value="all">Toàn bài</option>
                  {lesson.sections.map((s, i) => (
                    <option key={s.id} value={s.id}>
                      Phần {i + 1}: {s.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
                {previewSec === 'all' || !previewSection ? (
                  <BuilderPreview lesson={lesson} />
                ) : (
                  <div className="space-y-4">
                    <h2 className="text-lg font-black text-blue-900">{previewSection.title}</h2>
                    {previewSection.cards.length === 0 ? (
                      <p className="text-sm text-gray-400">Phần này chưa có thẻ.</p>
                    ) : (
                      previewSection.cards.map((card) => (
                        <div key={card.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                          <LessonCardRenderer card={card} />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default LessonBuilderPage
